import { MenuItem, TableCell, TableRow } from "@mui/material";
import { useState } from "react";
import { useMutation } from "react-query";
import BootstrapModal from "react-bootstrap/Modal";
import useLang from "Shared/hooks/useLanguage";
import { toast } from "react-toastify";
import {
  StyledDropButtonThreeDots,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import {
  InstrumentSave,
  InstrumentToggleStatus,
} from "Services/Instrument/Instrument";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";

function ActiveInstrumentRow({
  row,
  index,
  setLocalRows,
  refetch,
  setIsBtnDisabled,
}: any) {
  const { t } = useLang();
  const [anchorEl, setAnchorEl] = useState<any>({ dropdown1: null });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const isNewRow = row.instrumentId === 0;

  const handleClick = (event: any, dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: event.currentTarget });
  };

  const handleClose = (dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: null });
  };

  const handleChange = (name: string, value: string) => {
    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.map((x: any, i: number) =>
          i === index ? { ...x, [name]: value } : x
        )
      );
    } else {
      setLocalRows((curr: any[]) =>
        curr.map((x: any) =>
          x.instrumentId === row.instrumentId ? { ...x, [name]: value } : x
        )
      );
    }
  };

  const getValues = (r: any) => {
    setLocalRows((curr: any[]) => [
      { ...r, rowStatus: true },
      ...curr.filter((x: any) => x.instrumentId !== r.instrumentId),
    ]);
  };

  const handleRowCancel = () => {
    handleClose("dropdown1");
    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.filter((_: any, i: number) => i !== index)
      );
      setIsBtnDisabled(false);
    } else {
      setLocalRows((curr: any[]) =>
        curr.filter((x: any) => x.instrumentId !== row.instrumentId)
      );
    }
  };

  const removeFromLocalRows = () => {
    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.filter((_: any, i: number) => i !== index)
      );
    } else {
      setLocalRows((curr: any[]) =>
        curr.filter((x: any) => x.instrumentId !== row.instrumentId)
      );
    }
  };

  /* -------- useMutation: Save -------- */
  const saveMutation = useMutation(
    (payload: any) => InstrumentSave(payload),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 201 || res?.data?.statusCode === 200) {
          toast.success(res?.data?.data?.message);
          setIsBtnDisabled(false);
          handleClose("dropdown1");
          removeFromLocalRows();
          refetch();
        } else {
          toast.error(res?.data?.data?.message || t("Something went wrong."));
        }
      },
      onError: () => {
        toast.error(t("An unexpected error occurred."));
      },
    }
  );

  /* -------- useMutation: Deactivate -------- */
  const deactivateMutation = useMutation(
    (instrumentId: number) => InstrumentToggleStatus(instrumentId),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200) {
          toast.success(res?.data?.data?.message);
          handleClose("dropdown1");
          setShowDeactivateModal(false);
          refetch();
        } else {
          toast.error(res?.data?.data?.message || t("Something went wrong."));
        }
      },
      onError: () => {
        toast.error(t("An unexpected error occurred."));
      },
    }
  );

  const handleSave = () => {
    if (!row.instrumentName?.trim()) {
      toast.error(t("Instrument name cannot be empty."));
      return;
    }
    saveMutation.mutate({
      instrumentId: row.instrumentId ?? 0,
      instrumentName: row.instrumentName,
      instrumentDescription: row.instrumentDescription ?? "",
      instrumentType: row.instrumentType ?? "",
    });
  };

  const handleDeactivate = () => {
    if (!row.instrumentId) return toast.info(t("No Row Selected"));
    deactivateMutation.mutate(row.instrumentId);
  };

  const openDeactivateModal = () => {
    handleClose("dropdown1");
    setShowDeactivateModal(true);
  };

  return (
    <>
      <TableRow className="h-30px" key={row.instrumentId}>
        {/* Actions */}
        <TableCell>
          {row.rowStatus ? (
            <div className="d-flex justify-content-center">
              <div className="gap-2 d-flex">
                <button
                  disabled={saveMutation.isLoading}
                  id={`InstrumentActiveSave_${row.instrumentId}`}
                  className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                  onClick={handleSave}
                >
                  <i className="bi bi-check2" />
                </button>
                <button
                  id={`InstrumentActiveCancel_${row.instrumentId}`}
                  className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                  onClick={handleRowCancel}
                >
                  <i className="bi bi-x" />
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-center">
              <StyledDropButtonThreeDots
                id={`InstrumentActive3Dots_${row.instrumentId}`}
                aria-haspopup="true"
                onClick={(event: any) => handleClick(event, "dropdown1")}
                className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
              >
                <i className="bi bi-three-dots-vertical p-0 icon"></i>
              </StyledDropButtonThreeDots>
              <StyledDropMenuMoreAction
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl.dropdown1}
                open={Boolean(anchorEl.dropdown1)}
                onClose={() => handleClose("dropdown1")}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Instruments"
                  permissionIdentifier="Edit"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a
                        id={`InstrumentActiveEdit_${row.instrumentId}`}
                        className="w-150px p-0 text-dark"
                        onClick={() => { getValues(row); handleClose("dropdown1"); }}
                      >
                        <div className="menu-item px-3">
                          <i className="fa fa-edit text-primary mr-2 w-20px" />
                          {getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Edit"
                          )}
                        </div>
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Instruments"
                  permissionIdentifier="Inactive"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a
                        id={`InstrumentActiveDeactivate_${row.instrumentId}`}
                        className="w-150px p-0 text-dark"
                        onClick={openDeactivateModal}
                      >
                        <div className="menu-item px-3">
                          <i className="fa fa-ban text-danger mr-2 w-20px" />
                          {getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Inactive"
                          )}
                        </div>
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
              </StyledDropMenuMoreAction>
            </div>
          )}
        </TableCell>

        {/* Instrument Name */}
        <TableCell
          id={`InstrumentActiveName_${row.instrumentId}`}
          sx={{ width: "max-content" }}
        >
          {row.rowStatus ? (
            <input
              type="text"
              name="instrumentName"
              placeholder={t("Instrument Name")}
              className="form-control bg-white mb-lg-0 min-w-150px w-100 rounded h-30px"
              value={row.instrumentName ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.instrumentName}</div>
          )}
        </TableCell>

        {/* Instrument Description */}
        <TableCell
          id={`InstrumentActiveDescription_${row.instrumentId}`}
          sx={{ width: "max-content" }}
        >
          {row.rowStatus ? (
            <input
              type="text"
              name="instrumentDescription"
              placeholder={t("Instrument Description")}
              className="form-control bg-white mb-lg-0 min-w-150px w-100 rounded h-30px"
              value={row.instrumentDescription ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.instrumentDescription}</div>
          )}
        </TableCell>

        {/* Instrument Type */}
        <TableCell
          id={`InstrumentActiveType_${row.instrumentId}`}
          sx={{ width: "max-content" }}
        >
          {row.rowStatus ? (
            <input
              type="text"
              name="instrumentType"
              placeholder={t("Instrument Type")}
              className="form-control bg-white mb-lg-0 min-w-150px w-100 rounded h-30px"
              value={row.instrumentType ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.instrumentType}</div>
          )}
        </TableCell>
      </TableRow>

      {/* Deactivate Confirmation Modal */}
      <BootstrapModal
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <BootstrapModal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Inactive Instrument")}</h4>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {t("Are you sure you want to deactivate this instrument?")}
        </BootstrapModal.Body>
        <BootstrapModal.Footer className="p-0">
          <button
            id={`InstrumentActiveModalDeactivateCancel_${row.instrumentId}`}
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDeactivateModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            id={`InstrumentActiveModalDeactivateConfirm_${row.instrumentId}`}
            type="button"
            className="btn btn-danger m-2"
            onClick={handleDeactivate}
          >
            {t("Inactive")}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </>
  );
}

export default ActiveInstrumentRow;
